import React, { useState, useEffect } from "react"
import "./css/Header.css"
import "./css/Donate.css"
import img104 from "../assets/104-cat.jpg"
import img105 from "../assets/105.jpg"

const Donate = () => {
  return (
    <div className="donate-page">
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <div className="header-content">
            <h1 className="page-title">Support Our Mission</h1>
            <p className="page-subtitle">
              Help us plant trees, combat climate change, and support the Maasai community
            </p>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="impact-section">
        <div className="container">
          <div className="impact-content">
            <div className="impact-text">
              <h2 className="section-title">Make a Lasting Impact</h2>
              <div className="title-underline"></div>
              
              <div className="impact-description">
                <p className="lead-text">
                  Jambo Tree Kenya is raising funds to cover the cost of growing, planting and caring for trees by Maasai pastoral farmers who have been severely affected by recent droughts and famine. By reforesting we are helping restore ecosystems, combating climate change, while creating sustainable financial opportunities and protection of this indigenous community group.
                </p>
                
                <div className="highlight-box">
                  <h4>🌍 Kenya's Climate Goal</h4>
                  <p>We are also supporting Kenya's commitment to grow 15 billion trees by 2032.</p>
                </div>
                
                <p className="call-to-action">
                  <strong>Make a difference, and leave a legacy by donating to grow trees, which absorb CO2 in the air, and support in combating climate change and protection of the local habitat. You can also plant trees to offset your personal or companies CO2 emissions.</strong>
                </p>
              </div>
            </div>
            
            <div className="impact-images">
              <div className="image-gallery">
                <div className="image-large">
                  <img src={img105} alt="Maasai community tree planting" className="gallery-image" />
                </div>
                <div className="image-small">
                  <img src={img104} alt="Tree restoration project" className="gallery-image" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Donation Section */}
      <section className="donation-section">
        <div className="container">
          <div className="donation-content">
            <div className="donation-header">
              <h2 className="section-title">Plant Trees Today</h2>
              <div className="title-underline"></div>
              <p className="donation-description">
                Choose your donation amount and help us create a greener, more sustainable future.
              </p>
            </div>
            
            <DonationBox />
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-item">
              <h3 className="stat-number">15B</h3>
              <p className="stat-label">Kenya's Tree Goal by 2032</p>
            </div>
            <div className="stat-item">
              <h3 className="stat-number">100%</h3>
              <p className="stat-label">Community-Led Initiative</p>
            </div>
            <div className="stat-item">
              <h3 className="stat-number">CO2</h3>
              <p className="stat-label">Climate Change Mitigation</p>
            </div>
            <div className="stat-item">
              <h3 className="stat-number">Maasai</h3>
              <p className="stat-label">Community Support</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

const DonationBox = () => {
  const amounts = [
    {
      amount: 2,
      trees: 5
    },
    {
      amount: 50,
      trees: 10
    },
    {
      amount: 500,
      trees: 100
    },
    {
      amount: 5000,
      trees: 1000
    }
  ]

  const mpesaAmounts = [
    {
      amount: 1000,
      trees: 2
    },
    {
      amount: 2000,
      trees: 4
    },
    {
      amount: 5000,
      trees: 10
    }
  ]

  const [costs, setCosts] = useState(amounts)
  const [amount, setAmount] = useState(0)
  const [paymentMethod, setPaymentMethod] = useState("card")
  const [otherSelected, setOtherSelected] = useState(false)
  const [paymentFrequency, setPaymentFrequency] = useState("once")

  const handleSubmit = async event => {
    event.preventDefault()

    const formData = new FormData(event.target)
    const data = Object.fromEntries(formData.entries())
    delete data.card_number
    delete data.card_cvn

    const response = await fetch("https://jamboserver-production.up.railway.app", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ data: data })
    })

    const fields = await response.json()

    for (let [key, value] of Object.entries(fields)) {
      formData.set(key, value)
    }

    const form = document.createElement("form")
    form.method = "post"
    form.action = "https://secureacceptance.cybersource.com/pay"
    for (let [key, value] of formData) {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = key
      input.value = value
      form.appendChild(input)
    }
    document.body.appendChild(form)
    form.submit()
  }

  const disableButton = !amount

  useEffect(() => {
    if (paymentMethod === "mpesa") {
      setCosts(mpesaAmounts)
      setAmount(0)
    } else {
      setCosts(amounts)
      setAmount(0)
    }
  }, [paymentMethod])

  const currencySymbol = paymentMethod === "mpesa" ? "Ksh " : "$"

  return (
    <div className="donation-widget">
      <div className="donation-card">
        {/* Payment Frequency Tabs */}
        <div className="payment-tabs">
          <div 
            className={`tab ${paymentFrequency === "once" ? "active" : ""}`}
            onClick={() => setPaymentFrequency("once")}
          >
            <p>ONE-TIME DONATION</p>
            {paymentFrequency === "once" && <div className="tab-indicator" />}
          </div>
          <div className={`tab coming-soon ${paymentFrequency === "monthly" ? "active" : ""}`}>
            <p>
              MONTHLY DONATION 
              <span className="coming-soon-badge">COMING SOON</span>
            </p>
            {paymentFrequency === "monthly" && <div className="tab-indicator" />}
          </div>
        </div>

        {/* Donation Content */}
        <div className="donation-content">
          <h3 className="donation-title">
            {paymentFrequency === "once" ? "One-time Donation" : "Monthly Donation"}
          </h3>

          {/* Payment Method Selection */}
          <div className="payment-method-section">
            <h4>Payment Method</h4>
            <div className="payment-methods">
              <label className="payment-option">
                <input 
                  type="radio" 
                  value="card" 
                  name="paymentMethod" 
                  checked={paymentMethod === "card"} 
                  onChange={() => setPaymentMethod("card")} 
                />
                <span>Card (Visa or Mastercard)</span>
              </label>
              <label className="payment-option">
                <input 
                  type="radio" 
                  value="mpesa" 
                  name="paymentMethod" 
                  checked={paymentMethod === "mpesa"} 
                  onChange={() => setPaymentMethod("mpesa")} 
                />
                <span>Mpesa</span>
              </label>
            </div>
          </div>

          {/* Amount Selection */}
          <div className="amount-selection">
            <div className="amount-buttons">
              {costs.map(amt => (
                <button
                  key={amt.amount}
                  className={`amount-btn ${amount === amt.amount ? "selected" : ""}`}
                  onClick={() => setAmount(amt.amount)}
                >
                  <span className="amount">{currencySymbol}{amt.amount}</span>
                  <span className="trees">({amt.trees} trees)</span>
                </button>
              ))}
              <button
                className={`amount-btn other-btn ${amount === "other" ? "selected" : ""}`}
                onClick={() => setOtherSelected(!otherSelected)}
              >
                Other
              </button>
            </div>
            
            {otherSelected && (
              <div className="other-amount">
                <label htmlFor="custom-amount">Custom Amount</label>
                <input 
                  type="number" 
                  id="custom-amount"
                  className="custom-amount-input"
                  placeholder="Enter amount" 
                  onChange={e => setAmount(e.target.value)} 
                />
              </div>
            )}
          </div>

          {/* Payment Form */}
          <form className="donation-form" method="post" onSubmit={handleSubmit}>
            <input type="hidden" name="amount" value={amount} />
            <input type="hidden" name="currency" value="USD" />
            <input type="hidden" name="card_type" value="" />
            <input type="hidden" name="card_number" value="" />
            <input type="hidden" name="card_cvn" value="" />
            <input type="hidden" name="bill_to_forename" value="" />
            <input type="hidden" name="bill_to_surname" value="" />
            <input type="hidden" name="bill_to_address_line1" value="" />
            <input type="hidden" name="bill_to_address_city" value="" />
            <input type="hidden" name="bill_to_address_country" value="" />
            <input type="hidden" name="bill_to_email" value="" />
            <input type="hidden" name="card_expiry_date" value="" />
            <input type="hidden" name="locale" value="en-US" />
            <input type="hidden" name="payment_method" value="card" />

            {paymentMethod === "card" ? (
              <div className="donate-button-container">
                <button 
                  type="submit" 
                  className={`donate-button ${disableButton ? "disabled" : ""}`}
                  disabled={disableButton}
                >
                  Donate Now
                </button>
              </div>
            ) : (
              <div className="mpesa-info">
                <div className="mpesa-card">
                  <h4>📱 Mpesa Payment</h4>
                  <p>
                    <strong>JAMBO TREE LIMITED</strong><br />
                    Mpesa Till Number: <span className="till-number">8820210</span>
                  </p>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  )
}

export default Donate